import React from "react";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";

// Material UI
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";

// Icons
import CalculateIcon from "@mui/icons-material/Calculate";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import InventoryIcon from "@mui/icons-material/Inventory";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BoltIcon from "@mui/icons-material/Bolt";
import ShieldIcon from "@mui/icons-material/Shield";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.05 },
  },
};

const riseUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 180, damping: 22 },
  },
};

const products = [
  {
    id: "custos",
    title: "Custo Simples",
    tagline: "Controle financeiro descomplicado",
    description:
      "Gerencie custos, categorias e fornecedores em um so lugar. Lance notas fiscais, acompanhe dashboards e use o bot do WhatsApp para registrar gastos automaticamente.",
    icon: CalculateIcon,
    route: "/controle-custos",
    accent: "#059669",
    accentLight: "#10b981",
    gradient: "linear-gradient(145deg, #047857 0%, #10b981 100%)",
    features: [
      "Notas fiscais e categorias",
      "Dashboard analitico",
      "Bot WhatsApp integrado",
      "Gestao de fornecedores",
    ],
  },
  {
    id: "etiquetas",
    title: "Etiqueta Simples",
    tagline: "Etiquetas em poucos cliques",
    description:
      "Crie e gerencie etiquetas de forma rapida e eficaz. Escolha entre 6 modelos, controle validade e imprima direto pelo sistema.",
    icon: ConfirmationNumberIcon,
    route: "/etiqueta-simples",
    accent: "#d97706",
    accentLight: "#f59e0b",
    gradient: "linear-gradient(145deg, #b45309 0%, #f59e0b 100%)",
    features: [
      "6 modelos de etiqueta",
      "Controle de validade",
      "Impressao integrada",
      "Exportacao CSV",
    ],
  },
  {
    id: "estoque",
    title: "Estoque Simples",
    tagline: "Controle de estoque descomplicado",
    description:
      "Cadastre ingredientes, registre entradas e saidas, monte fichas tecnicas com custo automatico e receba alertas de estoque baixo.",
    icon: InventoryIcon,
    route: "/estoque-simples",
    accent: "#2563eb",
    accentLight: "#3b82f6",
    gradient: "linear-gradient(145deg, #1d4ed8 0%, #3b82f6 100%)",
    features: [
      "Controle de ingredientes",
      "Ficha tecnica com custos",
      "Entradas e saidas",
      "Alertas de estoque baixo",
    ],
  },
  {
    id: "funcionario",
    title: "Assistente Simples",
    tagline: "Automacao inteligente com IA",
    description:
      "Cadastre agentes IA para automatizar sua operacao. Defina permissoes, instrucoes personalizadas e limites de acao.",
    icon: SmartToyIcon,
    route: "/funcionario-simples",
    accent: "#1d4ed8",
    accentLight: "#2563eb",
    gradient: "linear-gradient(145deg, #1e3a8a 0%, #2563eb 100%)",
    features: [
      "Agentes IA personalizaveis",
      "Permissoes granulares por ferramenta",
      "Limite de acoes/hora",
      "Integracao nativa",
    ],
  },
  {
    id: "chef",
    title: "Chef por voz",
    tagline: "Um chef de IA guiando sua cozinha",
    description:
      "Na tela da cozinha, o funcionario marca o pedido como em preparo e o Chef conversa com ele passo a passo, usando a ficha tecnica de cada produto. Adicional ao plano.",
    icon: RecordVoiceOverIcon,
    route: "/cozinha",
    accent: "#b91c1c",
    accentLight: "#ef4444",
    gradient: "linear-gradient(145deg, #991b1b 0%, #ef4444 100%)",
    features: [
      "Conversa por voz, maos livres",
      "Quantidades da ficha tecnica",
      "Lembra observacoes do cliente",
      "Legendas para cozinha barulhenta",
    ],
  },
];

const pillars = [
  {
    icon: BoltIcon,
    title: "Rapido",
    text: "Ferramentas projetadas para agilizar seu dia a dia operacional.",
    color: "#f59e0b",
  },
  {
    icon: ShieldIcon,
    title: "Seguro",
    text: "Controle granular sobre permissoes, acessos e limites.",
    color: "#10b981",
  },
  {
    icon: TrendingUpIcon,
    title: "Escalavel",
    text: "Cresca sem complicacoes conforme a demanda aumenta.",
    color: "#2563eb",
  },
];

function ProductCard({ product }) {
  const theme = useTheme();
  const history = useHistory();
  const Icon = product.icon;

  return (
    <motion.div variants={riseUp} style={{ height: "100%" }}>
      <Paper
        elevation={0}
        onClick={() => history.push(product.route)}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: "6px",
          border: `1px solid ${alpha(product.accent, 0.12)}`,
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          "&:hover": {
            transform: "translateY(-10px)",
            boxShadow: `0 24px 48px -12px ${alpha(product.accent, 0.22)}`,
            borderColor: alpha(product.accent, 0.3),
            "& .card-accent-bar": {
              height: 8,
            },
            "& .card-icon-box": {
              transform: "scale(1.08) rotate(-3deg)",
              backgroundColor: alpha(product.accent, 0.12),
            },
            "& .card-cta-arrow": {
              transform: "translateX(6px)",
            },
          },
        }}
      >
        {/* Accent bar */}
        <Box
          className="card-accent-bar"
          sx={{
            height: 5,
            background: product.gradient,
            transition: "height 0.3s ease",
          }}
        />

        <Box
          sx={{
            p: { xs: 3, md: 3.5 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Icon + chip row */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 2.5 }}
          >
            <Box
              className="card-icon-box"
              sx={{
                width: 60,
                height: 60,
                borderRadius: "6px",
                backgroundColor: alpha(product.accent, 0.07),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.4s ease",
              }}
            >
              <Icon sx={{ fontSize: 30, color: product.accent }} />
            </Box>
            <Chip
              label="SIMPLES"
              size="small"
              sx={{
                backgroundColor: alpha(product.accent, 0.06),
                color: product.accent,
                fontWeight: 700,
                fontSize: "0.6rem",
                letterSpacing: "1.2px",
                height: 22,
                border: `1px solid ${alpha(product.accent, 0.15)}`,
              }}
            />
          </Stack>

          {/* Title block */}
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ mb: 0.25, color: theme.palette.text.primary }}
          >
            {product.title}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ mb: 2, color: product.accentLight }}
          >
            {product.tagline}
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2.5, lineHeight: 1.75, flex: 1 }}
          >
            {product.description}
          </Typography>

          {/* Feature checklist */}
          <Stack spacing={0.75} sx={{ mb: 3 }}>
            {product.features.map((f, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <CheckCircleOutlineIcon
                  sx={{
                    fontSize: 15,
                    color: product.accentLight,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.8rem" }}
                >
                  {f}
                </Typography>
              </Stack>
            ))}
          </Stack>

          {/* CTA */}
          <Button
            fullWidth
            variant="contained"
            endIcon={
              <ArrowForwardIcon
                className="card-cta-arrow"
                sx={{ transition: "transform 0.3s ease", fontSize: 18 }}
              />
            }
            sx={{
              mt: "auto",
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              py: 1.3,
              fontSize: "0.9rem",
              background: product.gradient,
              boxShadow: `0 4px 16px ${alpha(product.accent, 0.3)}`,
              "&:hover": {
                boxShadow: `0 8px 24px ${alpha(product.accent, 0.4)}`,
              },
            }}
          >
            Acessar
          </Button>
        </Box>
      </Paper>
    </motion.div>
  );
}

export default function SlapieSimples() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
        }}
      >
        {/* ─── HERO ─── */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            pt: { xs: 7, md: 12 },
            pb: { xs: 10, md: 16 },
            background:
              "linear-gradient(155deg, #0f172a 0%, #1e293b 35%, #1e3a5f 70%, #1e3a8a 100%)",
          }}
        >
          {/* Grid pattern overlay */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.03,
              backgroundImage:
                "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
              backgroundSize: "64px 64px",
              zIndex: 0,
            }}
          />

          {/* Glow orbs */}
          <Box
            component={motion.div}
            animate={{ x: [0, 30, 0], y: [0, -15, 0] }}
            transition={{
              duration: 14,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            sx={{
              position: "absolute",
              width: 500,
              height: 500,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 65%)",
              top: -120,
              right: -80,
              zIndex: 0,
            }}
          />
          <Box
            component={motion.div}
            animate={{ x: [0, -20, 0], y: [0, 20, 0] }}
            transition={{
              duration: 18,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            sx={{
              position: "absolute",
              width: 350,
              height: 350,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(245,158,11,0.1) 0%, transparent 65%)",
              bottom: -100,
              left: -50,
              zIndex: 0,
            }}
          />

          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <motion.div variants={stagger} initial="hidden" animate="visible">
              {/* Badge */}
              <motion.div variants={riseUp}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 1,
                      backgroundColor: alpha("#f59e0b", 0.5),
                    }}
                  />
                  <Typography
                    variant="overline"
                    sx={{
                      color: "#fbbf24",
                      fontWeight: 700,
                      letterSpacing: "3px",
                      fontSize: "0.7rem",
                    }}
                  >
                    SLAPIE SIMPLES
                  </Typography>
                  <Box
                    sx={{
                      width: 32,
                      height: 1,
                      backgroundColor: alpha("#f59e0b", 0.5),
                    }}
                  />
                </Stack>
              </motion.div>

              {/* Heading */}
              <motion.div variants={riseUp}>
                <Typography
                  variant={isMobile ? "h4" : "h2"}
                  fontWeight={800}
                  align="center"
                  sx={{
                    color: "#fff",
                    mb: 2.5,
                    letterSpacing: "-0.025em",
                    lineHeight: 1.15,
                  }}
                >
                  Ferramentas que{" "}
                  <Box
                    component="span"
                    sx={{
                      background:
                        "linear-gradient(90deg, #fbbf24, #f59e0b, #10b981)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    simplificam
                  </Box>
                  <br />
                  sua operacao
                </Typography>
              </motion.div>

              {/* Subtitle */}
              <motion.div variants={riseUp}>
                <Typography
                  variant="h6"
                  align="center"
                  fontWeight={400}
                  sx={{
                    color: alpha("#fff", 0.6),
                    maxWidth: 560,
                    mx: "auto",
                    mb: 6,
                    lineHeight: 1.7,
                    fontSize: { xs: "0.95rem", md: "1.1rem" },
                  }}
                >
                  Custos, estoque, etiquetas e automacao inteligente — tudo
                  integrado para voce focar no que importa.
                </Typography>
              </motion.div>

              {/* Pillars */}
              <motion.div variants={stagger}>
                <Grid container spacing={3} justifyContent="center">
                  {pillars.map((p, i) => {
                    const Icon = p.icon;
                    return (
                      <Grid item xs={12} sm={4} key={i}>
                        <motion.div variants={riseUp}>
                          <Stack
                            alignItems="center"
                            spacing={1.5}
                            sx={{ textAlign: "center" }}
                          >
                            <Box
                              sx={{
                                width: 52,
                                height: 52,
                                borderRadius: "50%",
                                background: alpha(p.color, 0.12),
                                border: `1px solid ${alpha(p.color, 0.2)}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Icon sx={{ fontSize: 24, color: p.color }} />
                            </Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              sx={{ color: "#fff" }}
                            >
                              {p.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: alpha("#fff", 0.5), maxWidth: 220 }}
                            >
                              {p.text}
                            </Typography>
                          </Stack>
                        </motion.div>
                      </Grid>
                    );
                  })}
                </Grid>
              </motion.div>
            </motion.div>
          </Container>

          {/* Angled separator */}
          <Box
            sx={{
              position: "absolute",
              bottom: -1,
              left: 0,
              right: 0,
              lineHeight: 0,
            }}
          >
            <svg
              viewBox="0 0 1440 80"
              preserveAspectRatio="none"
              style={{ width: "100%", height: 50, display: "block" }}
            >
              <path
                d="M0 80L1440 0V80H0Z"
                fill={theme.palette.background.default}
              />
            </svg>
          </Box>
        </Box>

        {/* ─── PRODUCTS GRID ─── */}
        <Box sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
          <Container maxWidth="lg">
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              <motion.div variants={riseUp}>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  align="center"
                  sx={{ mb: 1 }}
                >
                  Nossas Ferramentas
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                  sx={{ mb: 6, maxWidth: 460, mx: "auto" }}
                >
                  Cada ferramenta resolve um problema real do seu negocio de
                  forma simples e eficiente.
                </Typography>
              </motion.div>

              <Grid container spacing={4} alignItems="stretch">
                {products.map((product) => (
                  <Grid item xs={12} sm={6} md={3} key={product.id}>
                    <ProductCard product={product} />
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Container>
        </Box>

        {/* ─── FOOTER CTA ─── */}
        <Box
          sx={{
            py: { xs: 5, md: 7 },
            px: 2,
            backgroundColor: theme.palette.grey[50],
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Container maxWidth="sm">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <Stack alignItems="center" spacing={2}>
                <Typography variant="h5" fontWeight={700} align="center">
                  Precisa de ajuda para comecar?
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  align="center"
                  sx={{ maxWidth: 380 }}
                >
                  Acesse as Configuracoes Gerais para ativar e personalizar cada
                  ferramenta.
                </Typography>
                <Button
                  variant="outlined"
                  size="large"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                  onClick={() => history.push("/delivery-center")}
                  sx={{
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    px: 4,
                    mt: 1,
                  }}
                >
                  Ir para Configuracoes
                </Button>
              </Stack>
            </motion.div>
          </Container>
        </Box>
      </Box>
    </motion.div>
  );
}
